import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { fetchWithoutAuth } from '../libs/apiUtils'
import { 
  MagnifyingGlassIcon, 
  DocumentTextIcon, 
  FolderIcon, 
  TableCellsIcon, 
  CalendarIcon,
  BuildingOffice2Icon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import Pagination from '../components/Pagination'
import GlobalNavbar from '../components/GlobalNavbar'

interface TableData {
  id: string
  name: string
  description: string
  categoryId: string
  isPublic: boolean
  updatedAt?: string
  adminFullName?: string
}

interface GroupData {
  id: string
  name: string
  tableCount?: number
}

interface CategoryData {
  id: string
  name: string
  groupId: string
  tableCount?: number
}

export default function DatasetList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') || ''
  const groupId = searchParams.get('groupId') || ''
  const categoryId = searchParams.get('categoryId') || ''

  // Dataset Tables state
  const [tables, setTables] = useState<TableData[]>([])
  const [loading, setLoading] = useState(true)
  const [meta, setMeta] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  // Groups state
  const [groups, setGroups] = useState<GroupData[]>([])
  const [groupSearch, setGroupSearch] = useState('')
  const [groupPage, setGroupPage] = useState(1)
  const [groupMeta, setGroupMeta] = useState<any>(null)
  const [loadingGroups, setLoadingGroups] = useState(false)

  // Categories state
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [categorySearch, setCategorySearch] = useState('')
  const [categoryPage, setCategoryPage] = useState(1)
  const [categoryMeta, setCategoryMeta] = useState<any>(null)
  const [loadingCategories, setLoadingCategories] = useState(false)

  // 1. Fetch groups dynamically
  useEffect(() => {
    async function loadGroups() {
      try {
        setLoadingGroups(true)
        const res = await fetchWithoutAuth(`${process.env.REACT_APP_BACKEND_URL}/api/public/groups`, { 
          method: 'POST', 
          body: JSON.stringify({ page: groupPage, search: groupSearch }) 
        })
        const data = await res.json()
        if (res.ok && data.success && Array.isArray(data.data)) {
          setGroups(prev => groupPage === 1 ? data.data : [...prev, ...data.data])
          setGroupMeta(data.meta)
        } else {
          setGroups([])
          setGroupMeta(null)
        }
      } catch (err) {
        console.error('Failed to load groups', err)
        setGroups([])
        setGroupMeta(null)
      } finally {
        setLoadingGroups(false)
      }
    }

    const timer = setTimeout(() => loadGroups(), 300)
    return () => clearTimeout(timer)
  }, [groupSearch, groupPage])

  // 2. Fetch categories dynamically when groupId, search, or page changes
  useEffect(() => {
    async function loadCategories() {
      if (!groupId) {
        setCategories([])
        setCategoryMeta(null)
        return
      }

      try {
        setLoadingCategories(true)
        const res = await fetchWithoutAuth(`${process.env.REACT_APP_BACKEND_URL}/api/public/categories`, {
          method: 'POST',
          body: JSON.stringify({ groupId, page: categoryPage, search: categorySearch })
        })
        const data = await res.json()
        if (res.ok && data.success && Array.isArray(data.data)) {
          setCategories(prev => categoryPage === 1 ? data.data : [...prev, ...data.data])
          setCategoryMeta(data.meta)
        } else {
          setCategories([])
          setCategoryMeta(null)
        }
      } catch (err) {
        console.error('Failed to load categories:', err)
        setCategories([])
        setCategoryMeta(null)
      } finally {
        setLoadingCategories(false)
      }
    }

    const timer = setTimeout(() => loadCategories(), 300)
    return () => clearTimeout(timer)
  }, [groupId, categorySearch, categoryPage])

  // 3. Load dataset tables
  useEffect(() => {
    async function loadTables() {
      try {
        setLoading(true)
        const res = await fetchWithoutAuth(`${process.env.REACT_APP_BACKEND_URL}/api/public/tables`, {
          method: 'POST',
          body: JSON.stringify({
            page,
            perPage,
            search: q,
            groupId: groupId || undefined,
            categoryId: categoryId || undefined
          })
        })
        const data = await res.json()
        if (res.ok && data.success && Array.isArray(data.data)) {
          setTables(data.data)
          setMeta(data.meta)
        } else {
          setTables([])
          setMeta(null)
        }
      } catch (err) {
        console.error('Failed to load dataset tables', err)
        setTables([])
        setMeta(null)
      } finally {
        setLoading(false)
      }
    }
    loadTables()
  }, [q, groupId, categoryId, page, perPage])

  // Handlers
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const newQ = (formData.get('q') as string) || ''
    setSearchParams(prev => {
      if (newQ) prev.set('q', newQ)
      else prev.delete('q')
      prev.delete('page')
      return prev
    })
    setPage(1)
  }

  const handleFilterGroup = (id: string) => {
    setSearchParams(prev => {
      if (id) prev.set('groupId', id)
      else prev.delete('groupId')
      prev.delete('categoryId')
      prev.delete('page')
      return prev
    })
    setPage(1)
    setCategorySearch('')
    setCategoryPage(1)
  }

  const handleFilterCategory = (id: string) => {
    setSearchParams(prev => {
      if (id) prev.set('categoryId', id)
      else prev.delete('categoryId')
      prev.delete('page')
      return prev
    })
    setPage(1)
  }

  const handleResetFilters = () => {
    setSearchParams({})
    setPage(1)
    setGroupSearch('')
    setCategorySearch('')
  }

  const selectedGroup = groups.find(g => g.id === groupId)
  const selectedCategory = categories.find(c => c.id === categoryId)

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col font-sans">
      <GlobalNavbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1">
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Sidebar Filters */}
          <aside className="w-full md:w-80 flex-shrink-0 space-y-6">
            
            {/* Group Filter Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-neutral-200/80 p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-neutral-900 flex items-center gap-2">
                  <FolderIcon className="size-5 text-primary-600"/>
                  Grup Data
                </h3>
                {groupMeta && (
                  <span className="bg-neutral-100 text-neutral-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {groupMeta.total}
                  </span>
                )}
              </div>
              
              <div className="mb-3 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-400"/>
                <input 
                  type="text" 
                  placeholder="Cari grup data..." 
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none bg-neutral-50/50 focus:bg-white"
                  value={groupSearch}
                  onChange={(e) => {
                    setGroupSearch(e.target.value)
                    setGroupPage(1)
                  }}
                />
              </div>

              <div className="space-y-1 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                <button 
                  onClick={() => handleFilterGroup('')}
                  className={`w-full flex justify-between items-center px-3 py-2 rounded-xl text-xs font-medium transition-all ${!groupId ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-neutral-600 hover:bg-neutral-100/80'}`}
                >
                  <span>Semua Grup Data</span>
                </button>

                {loadingGroups && groupPage === 1 ? (
                  <div className="py-6 text-center text-xs text-neutral-400 flex items-center justify-center gap-2">
                    <ArrowPathIcon className="size-4 animate-spin"/> Memuat grup...
                  </div>
                ) : (
                  Array.isArray(groups) && groups.map(g => (
                    <button 
                      key={g.id}
                      onClick={() => handleFilterGroup(g.id)}
                      className={`w-full flex justify-between items-center px-3 py-2 rounded-xl text-xs transition-all ${groupId === g.id ? 'bg-primary-50 text-primary-700 font-semibold border-l-4 border-primary-600 pl-2' : 'text-neutral-600 hover:bg-neutral-100/80'}`}
                    >
                      <span className="truncate pr-2">{g.name}</span>
                      {g.tableCount !== undefined && (
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${groupId === g.id ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500'}`}>
                          {g.tableCount}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
              
              {groupMeta && groupPage < groupMeta.lastPage && (
                <button 
                  onClick={() => setGroupPage(p => p + 1)}
                  className="w-full mt-3 text-xs text-primary-600 hover:text-primary-700 font-semibold py-2 border-t border-neutral-100 transition-colors"
                >
                  Tampilkan lebih banyak
                </button>
              )}
            </div>

            {/* Category Filter Card (Conditional) */}
            {groupId && (
              <div className="bg-white rounded-2xl shadow-xs border border-neutral-200/80 p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-bold text-neutral-900 flex items-center gap-2">
                    <DocumentTextIcon className="size-5 text-primary-600"/>
                    Kategori
                  </h3>
                  {categoryMeta && (
                    <span className="bg-neutral-100 text-neutral-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                      {categoryMeta.total}
                    </span>
                  )}
                </div>

                <div className="mb-3 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-400"/>
                  <input 
                    type="text" 
                    placeholder="Cari kategori..." 
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none bg-neutral-50/50 focus:bg-white"
                    value={categorySearch}
                    onChange={(e) => {
                      setCategorySearch(e.target.value)
                      setCategoryPage(1)
                    }}
                  />
                </div>

                <div className="space-y-1 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                  <button 
                    onClick={() => handleFilterCategory('')}
                    className={`w-full flex justify-between items-center px-3 py-2 rounded-xl text-xs font-medium transition-all ${!categoryId ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-neutral-600 hover:bg-neutral-100/80'}`}
                  >
                    <span>Semua Kategori</span>
                  </button>

                  {loadingCategories && categoryPage === 1 ? (
                    <div className="py-6 text-center text-xs text-neutral-400 flex items-center justify-center gap-2">
                      <ArrowPathIcon className="size-4 animate-spin"/> Memuat kategori...
                    </div>
                  ) : (
                    Array.isArray(categories) && categories.map(c => (
                      <button 
                        key={c.id}
                        onClick={() => handleFilterCategory(c.id)}
                        className={`w-full flex justify-between items-center px-3 py-2 rounded-xl text-xs transition-all ${categoryId === c.id ? 'bg-primary-50 text-primary-700 font-semibold border-l-4 border-primary-600 pl-2' : 'text-neutral-600 hover:bg-neutral-100/80'}`}
                      >
                        <span className="truncate pr-2">{c.name}</span>
                        {c.tableCount !== undefined && (
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${categoryId === c.id ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500'}`}>
                            {c.tableCount}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>

                {categoryMeta && categoryPage < categoryMeta.lastPage && (
                  <button 
                    onClick={() => setCategoryPage(p => p + 1)}
                    className="w-full mt-3 text-xs text-primary-600 hover:text-primary-700 font-semibold py-2 border-t border-neutral-100 transition-colors"
                  >
                    Tampilkan lebih banyak
                  </button>
                )}
              </div>
            )}
          </aside>

          {/* Main Dataset Area */}
          <div className="flex-1 space-y-6">
            
            {/* Search Input Bar */}
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-neutral-400"/>
                <input 
                  type="text" 
                  name="q"
                  defaultValue={q}
                  placeholder="Cari kata kunci dataset (misal: Jumlah Penduduk)..." 
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-neutral-200 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none shadow-xs text-sm"
                />
              </div>
              <button 
                type="submit" 
                className="bg-primary-600 hover:bg-primary-700 text-white px-7 py-3 rounded-2xl font-semibold text-sm transition-colors shadow-xs flex items-center gap-2"
              >
                Cari
              </button>
            </form>

            {/* Active Filters Bar */}
            {(q || groupId || categoryId) && (
              <div className="bg-primary-50/60 border border-primary-100 rounded-2xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-primary-900">Filter Aktif:</span>
                  
                  {q && (
                    <span className="inline-flex items-center gap-1 bg-white border border-primary-200 text-primary-800 px-3 py-1 rounded-full font-medium">
                      Pencarian: "{q}"
                    </span>
                  )}
                  {selectedGroup && (
                    <span className="inline-flex items-center gap-1 bg-white border border-primary-200 text-primary-800 px-3 py-1 rounded-full font-medium">
                      Grup: {selectedGroup.name}
                    </span>
                  )}
                  {selectedCategory && (
                    <span className="inline-flex items-center gap-1 bg-white border border-primary-200 text-primary-800 px-3 py-1 rounded-full font-medium">
                      Kategori: {selectedCategory.name}
                    </span>
                  )}
                </div>

                <button 
                  onClick={handleResetFilters}
                  className="text-primary-700 hover:text-primary-900 font-semibold flex items-center gap-1 hover:underline ml-auto"
                >
                  <XMarkIcon className="size-4" /> Reset Semua Filter
                </button>
              </div>
            )}

            {/* Dataset Cards List */}
            <div className="bg-white rounded-2xl shadow-xs border border-neutral-200/80 overflow-hidden">
              <div className="p-4 px-6 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
                <h2 className="font-bold text-neutral-800 text-sm">
                  {meta?.total || 0} Dataset Ditemukan
                </h2>
              </div>
              
              <div className="divide-y divide-neutral-100">
                {loading ? (
                  <div className="p-16 text-center text-neutral-400 flex flex-col items-center gap-3">
                    <ArrowPathIcon className="size-8 animate-spin text-primary-600"/>
                    <p className="text-sm font-medium">Memuat data dataset...</p>
                  </div>
                ) : !Array.isArray(tables) || tables.length === 0 ? (
                  <div className="p-16 text-center text-neutral-400">
                    <TableCellsIcon className="size-12 mx-auto text-neutral-300 mb-3"/>
                    <p className="text-base font-semibold text-neutral-700">Tidak ada dataset ditemukan</p>
                    <p className="text-xs text-neutral-500 mt-1">Coba sesuaikan kata kunci atau filter pencarian Anda.</p>
                  </div>
                ) : (
                  tables.map(table => (
                    <article key={table.id} className="p-6 hover:bg-primary-50/30 transition-all group">
                      <Link className="block" to={`/dataset/${table.id}`}>
                        <h3 className="text-lg font-bold text-primary-600 group-hover:text-primary-800 mb-2 transition-colors leading-snug">
                          {table.name}
                        </h3>
                        
                        <p className="text-neutral-600 text-sm line-clamp-2 mb-4 leading-relaxed">
                          {table.description && table.description !== '-' 
                            ? table.description 
                            : 'Tidak ada deskripsi yang tersedia untuk dataset ini.'}
                        </p>

                        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 text-xs text-neutral-500 border-t border-neutral-100/80">
                          <div className="flex flex-wrap items-center gap-2">
                            {table.adminFullName && (
                              <span className="inline-flex items-center gap-1.5 bg-neutral-100 text-neutral-700 px-2.5 py-1 rounded-lg font-medium">
                                <BuildingOffice2Icon className="size-3.5 text-neutral-400" />
                                {table.adminFullName}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-2.5 py-0.5 rounded-md font-semibold text-[11px]">
                              XLSX
                            </span>
                          </div>

                          {table.updatedAt && (
                            <span className="inline-flex items-center gap-1.5 text-neutral-400">
                              <CalendarIcon className="size-4 text-neutral-400"/>
                              Diperbarui {new Date(table.updatedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </Link>
                    </article>
                  ))
                )}
              </div>
              
              {/* Pagination */}
              {meta && (
                <div className="p-4 border-t border-neutral-100 bg-neutral-50/50">
                  <Pagination 
                    currentPage={page} 
                    lastPage={meta.lastPage} 
                    total={meta.total}
                    perPage={perPage}
                    onPageChange={setPage} 
                    onPerPageChange={(newPerPage) => {
                      setPerPage(newPerPage)
                      setPage(1)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
